import i18n from '../i18n';
import { intlLocales, isLanguage } from '../i18n/language';

/**
 * These read the active language at call time rather than taking it as an argument, which keeps
 * every call site unchanged. That works because a component showing a formatted value also shows
 * translated labels, so it re-renders on a language change anyway — but it does mean the output is
 * language-dependent: never cache a formatted result across renders without depending on `t`.
 */
function currentLocale(): string {
  const language = i18n.resolvedLanguage;
  return isLanguage(language) ? intlLocales[language] : intlLocales.en;
}

const dateTimeFormats = new Map<string, Intl.DateTimeFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();
const amountFormats = new Map<string, Intl.NumberFormat>();

function cached<T>(cache: Map<string, T>, key: string, create: () => T): T {
  const existing = cache.get(key);
  if (existing) return existing;

  const created = create();
  cache.set(key, created);
  return created;
}

const byteUnits = ['B', 'kB', 'MB', 'GB', 'TB'] as const;

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;

  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < byteUnits.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value.toFixed(value < 10 ? 1 : 0)} ${byteUnits[unit]}`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const locale = currentLocale();
  const format = cached(
    dateTimeFormats,
    locale,
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
  );

  return format.format(parsed);
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  // The gate sends plain yyyy-MM-dd; parsing it as a Date would shift it by the local offset.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.exec(value);
  if (dateOnly) return value;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const locale = currentLocale();
  const format = cached(dateFormats, locale, () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }));

  return format.format(parsed);
}

export function formatAmount(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (!currency) return value.toFixed(2);

  const locale = currentLocale();

  try {
    const format = cached(
      amountFormats,
      `${locale}:${currency}`,
      () => new Intl.NumberFormat(locale, { style: 'currency', currency }),
    );

    return format.format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

/** Turns a gate status such as `failed_delivery` into `Failed delivery`. */
export function humanizeGateStatus(status: string): string {
  if (status.length === 0) return status;
  const spaced = status.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * How an account reads in a picker. One tenant can hold several accounts of similar names, so the
 * Peppol participant id — the address the network knows the sender by — is what tells them apart.
 */
export function accountLabel(account: { name: string; participantId: string | null }): string {
  return account.participantId ? `${account.name} · ${account.participantId}` : account.name;
}

/**
 * The deployed version as the drawer footer shows it: the semantic version alone. Whatever build
 * metadata follows `+` (branch, commit sha — the pipeline's business) is dropped from the label;
 * the footer's tooltip carries the raw value for anyone who needs the exact commit.
 */
export function formatDeployedVersion(raw: string): string {
  const separator = raw.indexOf('+');
  return separator < 0 ? raw : raw.slice(0, separator);
}

export function shortHash(sha256: string | null | undefined, length = 12): string {
  if (!sha256) return '—';
  return sha256.length <= length ? sha256 : sha256.slice(0, length);
}

/** `yyyy-MM-dd` for a `TextField type="date"`, which refuses any other format. */
export function toDateInputValue(value: Date): string {
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}
